import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import AuthHeader from "@/components/AuthHeader";
import { PrimaryButton } from "@/components/brand";
import GoogleButton from "@/components/GoogleButton";
import { useAuth } from "@/context/AuthContext";
import { LOGIN } from "@/constants/testIds";
import Footer from "@/components/Footer";

const ROLE_COPY = {
  driver: { tag: "DRIVER", title: "Driver\nsign-in.", sub: "Sign in to your Routa driver portal." },
  merchant: { tag: "MERCHANT", title: "Merchant\nsign-in.", sub: "Sign in to your Routa merchant console." },
  admin: { tag: "ADMIN", title: "Operations\nsign-in.", sub: "Sign in to the Routa operations console." },
};

export default function Login() {
  const [params] = useSearchParams();
  const role = ROLE_COPY[params.get("role")] ? params.get("role") : "driver";
  const copy = ROLE_COPY[role];
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) return setError(res.error);
    const dest = res.user.role === "admin" ? "/admin" : res.user.role === "merchant" ? "/merchant" : "/driver";
    navigate(dest, { replace: true });
  };

  const others = Object.keys(ROLE_COPY).filter((r) => r !== role);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <AuthHeader tag="SECURE LOGIN" />
      <div className="flex-1 flex items-center">
        <div className="mx-auto w-full max-w-md px-6 py-16">
          <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400 mb-4">[{copy.tag}]</div>
          <h1 className="headline text-5xl text-[#0a0a0a] whitespace-pre-line">{copy.title}</h1>
          <p className="mt-5 text-base text-gray-500">{copy.sub}</p>

          <form onSubmit={submit} className="mt-10 space-y-6">
            {error && (
              <div data-testid="login-error" className="border border-orange-500 bg-orange-50 text-orange-700 text-sm px-4 py-3">
                {error}
              </div>
            )}
            <div>
              <label className="routa-label">Email</label>
              <input
                data-testid={LOGIN.emailInput}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="routa-input"
              />
            </div>
            <div>
              <label className="routa-label">Password</label>
              <input
                data-testid={LOGIN.passwordInput}
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="routa-input"
              />
            </div>
            <PrimaryButton data-testid={LOGIN.submitButton} type="submit" disabled={busy} className="w-full">
              {busy ? "Signing in…" : "Sign in"}
            </PrimaryButton>
          </form>

          {role !== "admin" && (
            <div className="mt-6">
              <div className="flex items-center gap-4 mb-4">
                <span className="h-px flex-1 bg-gray-200" />
                <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-gray-400">or</span>
                <span className="h-px flex-1 bg-gray-200" />
              </div>
              <GoogleButton intent={role} />
            </div>
          )}

          <div className="mt-6 flex items-center justify-between">
            <Link data-testid={LOGIN.forgotPasswordLink} to="/forgot-password" className="text-sm text-gray-500 hover:text-[#0a0a0a]">
              Forgot password?
            </Link>
            <div className="flex gap-4">
              {others.map((r) => (
                <Link
                  key={r}
                  to={`/login?role=${r}`}
                  data-testid={`login-switch-${r}`}
                  className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400 hover:text-[#0a0a0a]"
                >
                  {r}
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-8 text-sm text-gray-500">
            {role === "merchant" ? (
              <>Need a merchant account? <Link to="/register" data-testid="login-register-link" className="underline text-[#0a0a0a]">Create one</Link>.</>
            ) : (
              <>Not yet a Routa driver? <Link to="/apply" data-testid="login-apply-link" className="underline text-[#0a0a0a]">Apply here</Link>.</>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
